/**
 * Costs sub-tab — AWS spend by service (Stage 7 Phase 1, GET /costs).
 *
 * Implementation plan: D7.8–D7.15
 */

import { useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '@/api/baseUrl';
import { ApiError, getCosts } from '@/api/client';
import { POLL_INTERVAL_COSTS_MS, useRefresh } from '@/context/RefreshContext';
import type {
  CostBreakdownAvailability,
  CostBreakdownMeta,
  CostByAwsService,
  CostSummary,
  CostsResponse,
} from '@/types/api';
import { formatShortDate, formatWhen } from './costsTabFormat';
import {
  buildPieSlices,
  formatCurrency,
  partitionServicesForPieChart,
  pieSlicePath,
} from './costsTabPie';

const BREAKDOWN_AVAILABILITY_HEADINGS: Record<CostBreakdownAvailability, string> = {
  available: 'Line items loaded',
  not_applicable: 'Breakdown not applicable',
  below_cost_threshold: 'Below enrichment threshold',
  outside_enrichment_cap: 'Outside top services limit',
  cost_explorer_empty: 'No grouped rows from Cost Explorer',
  cost_explorer_error: 'Cost Explorer request failed',
};

function breakdownMetaShowsLongDetail(meta: CostBreakdownMeta, hasBreakdownLines: boolean): boolean {
  if (meta.detail === undefined || meta.detail === '') {
    return false;
  }
  if (meta.availability === 'available' && hasBreakdownLines) {
    return false;
  }
  return true;
}

function PieSliceBreakdownMetaSection({
  meta,
  hasBreakdownLines,
}: {
  meta: CostBreakdownMeta;
  hasBreakdownLines: boolean;
}): JSX.Element {
  return (
    <div className="costs-pie-tooltip-meta-section">
      <p className="costs-pie-tooltip-sub">Breakdown status</p>
      <p className="costs-pie-meta-heading">{BREAKDOWN_AVAILABILITY_HEADINGS[meta.availability]}</p>
      {breakdownMetaShowsLongDetail(meta, hasBreakdownLines) ? (
        <p className="costs-pie-meta-detail">{meta.detail}</p>
      ) : null}
      {meta.code !== undefined && meta.code !== '' ? (
        <p className="costs-pie-meta-code">
          <span className="sr-only">Error code </span>
          {meta.code}
        </p>
      ) : null}
      {meta.attemptedDimensions !== undefined && meta.attemptedDimensions.length > 0 ? (
        <p className="costs-pie-meta-dims">
          Cost Explorer dimensions tried (in order): {meta.attemptedDimensions.join(' → ')}
        </p>
      ) : null}
    </div>
  );
}

/** Extracts a readable message from an API error response body (JSON or plain text). */
function formatApiErrorBody(body: unknown): string | null {
  if (body === undefined || body === null) {
    return null;
  }
  if (typeof body !== 'string') {
    try {
      return JSON.stringify(body);
    } catch {
      return Object.prototype.toString.call(body);
    }
  }
  const t = body.trim();
  if (t === '') {
    return null;
  }
  try {
    const o = JSON.parse(t) as { message?: unknown; error?: unknown };
    const msg = o.message ?? o.error;
    if (typeof msg === 'string' && msg.trim() !== '') {
      return msg.trim();
    }
  } catch {
    /* not JSON */
  }
  const max = 500;
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

/**
 * When fetch() fails before an HTTP response, browsers throw TypeError with
 * engine-specific messages (e.g. Chromium: "Failed to fetch", Safari: "Load failed").
 */
function appendClientFetchFailureHint(err: Error, lines: string[]): void {
  if (err.name !== 'TypeError') {
    return;
  }
  const m = err.message.trim().toLowerCase();
  const looksLikeFetchFailure =
    m === 'failed to fetch' ||
    m === 'load failed' ||
    m.includes('failed to fetch') ||
    m.includes('load failed') ||
    m.includes('networkerror');

  if (!looksLikeFetchFailure) {
    return;
  }

  lines.push(
    'Hint: When Pipeline, Services, Database, and Alerts load but Costs does not, your session and API host already work for other routes — narrow troubleshooting to GET /costs only. Confirm observability-api Stage 7 deploy wires GET /costs in API Gateway (or equivalent), that CORS and the JWT authorizer apply to this path like /pipeline and /services, the costs Lambda is attached and not timing out before a response, and no WAF or edge rule blocks /costs alone.',
  );

  lines.push(
    'Hint: If every tab fails the same way (or you are unsure): the browser never received an HTTP response. Typical causes include API or network outage, CORS blocking ops.blooph.com, TLS/certificate issues, VPN or proxy, or mixed content (HTTPS page calling HTTP). Safari often reports "Load failed"; Chromium usually says "Failed to fetch".',
  );
}

/** Builds a multi-line explanation for the Costs tab when GET /costs fails. */
function formatCostsLoadError(err: unknown): string {
  const lines: string[] = [];

  try {
    lines.push(`API base: ${getApiBaseUrl()}`);
  } catch (configErr: unknown) {
    lines.push(
      `Configuration: ${configErr instanceof Error ? configErr.message : String(configErr)}`,
    );
  }

  lines.push('Request: GET /costs');

  if (err instanceof ApiError) {
    lines.push(`HTTP status: ${err.status}`);
    lines.push(`Details: ${err.message}`);
    const bodyText = formatApiErrorBody(err.body);
    if (bodyText !== null) {
      lines.push(`Response body: ${bodyText}`);
    }
    if (err.status === 401) {
      lines.push(
        'Hint: Session may have expired — try logging out and signing in again, then open the Costs tab.',
      );
    } else if (err.status === 403) {
      lines.push('Hint: Your account may not be authorized to access cost data from the API.');
    } else if (err.status === 404) {
      lines.push(
        'Hint: The costs endpoint is missing on this API (deploy observability-api Stage 7 / GET /costs).',
      );
    } else if (err.status === 503) {
      lines.push('Hint: Cost Explorer or the costs Lambda may be unavailable; retry later.');
    }
    return lines.join('\n');
  }

  if (err instanceof Error) {
    lines.push(`Client error (${err.name}): ${err.message}`);
    appendClientFetchFailureHint(err, lines);
    return lines.join('\n');
  }

  lines.push(`Unknown error: ${String(err)}`);
  return lines.join('\n');
}

function lastUtcDayOfMonth(year: number, monthIndex0: number): number {
  return new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate();
}

/** Parses `YYYY-MM-DD` as a UTC calendar date. */
function parseDailyCostDateYmd(ymd: string): { y: number; m0: number; d: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (m === null) {
    return null;
  }
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) {
    return null;
  }
  const m0 = mo - 1;
  const check = new Date(Date.UTC(y, m0, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== m0 || check.getUTCDate() !== d) {
    return null;
  }
  return { y, m0, d };
}

/**
 * Sums prior-month daily costs through the same calendar day as `currentPeriodEnd` (UTC),
 * capped to the prior month’s length (e.g. Mar 1–28 vs Feb 1–28 when “today” is Mar 31).
 */
function samePeriodPreviousMonthSpend(
  previous: CostSummary,
  currentPeriodEndIso: string,
): number | null {
  const periodEnd = new Date(currentPeriodEndIso);
  if (Number.isNaN(periodEnd.getTime())) {
    return null;
  }
  const prevStart = new Date(previous.periodStart);
  if (Number.isNaN(prevStart.getTime())) {
    return null;
  }
  const py = prevStart.getUTCFullYear();
  const pm = prevStart.getUTCMonth();
  const targetDom = periodEnd.getUTCDate();
  const capDom = Math.min(targetDom, lastUtcDayOfMonth(py, pm));

  const daily = previous.dailyCosts;
  if (daily.length === 0) {
    return null;
  }

  let sum = 0;
  for (const row of daily) {
    const parsed = parseDailyCostDateYmd(row.date);
    if (parsed === null) {
      continue;
    }
    if (parsed.y !== py || parsed.m0 !== pm) {
      continue;
    }
    if (parsed.d > capDom) {
      continue;
    }
    sum += row.cost;
  }
  return sum;
}

function formatSamePeriodRangeSubtitle(current: CostSummary, previous: CostSummary): string {
  const periodEnd = new Date(current.periodEnd);
  const prevStart = new Date(previous.periodStart);
  const py = prevStart.getUTCFullYear();
  const pm = prevStart.getUTCMonth();
  const capDom = Math.min(
    periodEnd.getUTCDate(),
    lastUtcDayOfMonth(py, pm),
  );
  const prevEndIso = new Date(Date.UTC(py, pm, capDom)).toISOString();
  return `${formatShortDate(current.periodStart)} – ${formatShortDate(current.periodEnd)} vs ${formatShortDate(previous.periodStart)} – ${formatShortDate(prevEndIso)}`;
}

/** Compares current MTD to the same calendar span in the prior month (from `dailyCosts`). */
function samePeriodMonthOverMonthCard(
  current: CostSummary,
  previous: CostSummary,
): { primary: string; secondary?: string } {
  const currentMtd = current.totalCost;

  if (previous.totalCost <= 0) {
    if (currentMtd > 0) {
      return { primary: 'New spend (no prior month)' };
    }
    return { primary: '—' };
  }

  const prevSamePeriod = samePeriodPreviousMonthSpend(previous, current.periodEnd);
  if (prevSamePeriod === null) {
    return {
      primary: '—',
      secondary:
        'Same-period comparison unavailable — prior month has no daily breakdown from Cost Explorer.',
    };
  }

  if (prevSamePeriod <= 0) {
    if (currentMtd > 0) {
      return {
        primary: 'New spend in this window',
        secondary: formatSamePeriodRangeSubtitle(current, previous),
      };
    }
    return { primary: '—', secondary: formatSamePeriodRangeSubtitle(current, previous) };
  }

  const pct = ((currentMtd - prevSamePeriod) / prevSamePeriod) * 100;
  const rounded = Math.round(pct * 10) / 10;
  const sign = rounded > 0 ? '+' : '';
  return {
    primary: `${sign}${rounded}% vs same days last month`,
    secondary: formatSamePeriodRangeSubtitle(current, previous),
  };
}

function sortDailyAscending(summary: CostSummary): { date: string; cost: number; currency: string }[] {
  return [...summary.dailyCosts].sort((a, b) => a.date.localeCompare(b.date));
}

function sortServicesByCost(summary: CostSummary): CostByAwsService[] {
  return [...summary.costByAwsService].sort((a, b) => b.cost - a.cost);
}

export function CostsAwsServiceTab(): JSX.Element {
  const { mode, manualRefreshNonce, tabVisible, requestManualRefresh } = useRefresh();
  const [data, setData] = useState<CostsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pieHoverKey, setPieHoverKey] = useState<string | null>(null);

  useEffect(() => {
    if (!tabVisible) {
      return undefined;
    }

    let cancelled = false;

    function run(silent: boolean): void {
      if (cancelled) {
        return;
      }
      if (!silent) {
        setIsLoading(true);
        setError(null);
      }

      void getCosts()
        .then((response) => {
          if (!cancelled) {
            setData(response);
          }
        })
        .catch((err: unknown) => {
          if (cancelled || silent) {
            return;
          }
          setError(formatCostsLoadError(err));
        })
        .finally(() => {
          if (!cancelled && !silent) {
            setIsLoading(false);
          }
        });
    }

    run(false);

    let intervalId: number | undefined;
    if (mode === 'auto') {
      intervalId = window.setInterval(() => run(true), POLL_INTERVAL_COSTS_MS);
    }

    return () => {
      cancelled = true;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [mode, tabVisible, manualRefreshNonce]);

  const currency = data?.currentMonth.currency ?? 'USD';
  const sortedServices = useMemo(
    () => (data !== null ? sortServicesByCost(data.currentMonth) : []),
    [data],
  );
  const pieSlices = useMemo(() => {
    if (sortedServices.length === 0) {
      return [];
    }
    const { main, other } = partitionServicesForPieChart(sortedServices);
    return buildPieSlices(main, other, currency);
  }, [sortedServices, currency]);

  useEffect(() => {
    setPieHoverKey(null);
  }, [pieSlices]);

  const hoveredPieSlice =
    pieHoverKey !== null ? pieSlices.find((s) => s.key === pieHoverKey) ?? null : null;

  const sortedDaily = useMemo(
    () => (data !== null ? sortDailyAscending(data.currentMonth) : []),
    [data],
  );
  const maxDaily = useMemo(
    () => sortedDaily.reduce((m, d) => Math.max(m, d.cost), 0),
    [sortedDaily],
  );
  const monthCompareScale = useMemo(() => {
    if (data === null) {
      return { max: 1, mtd: 0, forecastRest: 0, projected: 0, previous: 0, forecastCurrency: 'USD' };
    }
    const mtd = data.currentMonth.totalCost;
    const forecastRest = data.forecast.forecastedCost;
    const projected = mtd + forecastRest;
    const previous = data.previousMonth.totalCost;
    const max = Math.max(projected, previous, 1);
    const forecastCurrency = data.forecast.currency || data.currentMonth.currency;
    return { max, mtd, forecastRest, projected, previous, forecastCurrency };
  }, [data]);

  const momCard = useMemo(
    () => (data !== null ? samePeriodMonthOverMonthCard(data.currentMonth, data.previousMonth) : null),
    [data],
  );

  return (
    <div className="costs-subpanel">
      <div className="costs-toolbar" role="group" aria-label="AWS service costs refresh">
        {!isLoading && error === null && data !== null && (
          <p className="costs-last-updated">
            Last updated {formatWhen(data.lastUpdated)}
          </p>
        )}
        <button
          type="button"
          className="costs-refresh-btn"
          onClick={(): void => requestManualRefresh()}
          disabled={isLoading}
        >
          Refresh
        </button>
      </div>

      {isLoading && <p className="placeholder">Loading AWS service costs…</p>}
      {!isLoading && error !== null && (
        <div className="costs-error" role="alert">
          <p className="costs-error-title">Could not load AWS service cost data</p>
          <pre className="costs-error-detail">{error}</pre>
        </div>
      )}
      {!isLoading && error === null && data !== null && (
        <>
          <section className="costs-summary-grid" aria-label="Cost summary">
            <article className="costs-summary-card">
              <h3 className="costs-card-title">Current month</h3>
              <p className="costs-card-value">{formatCurrency(data.currentMonth.totalCost, currency)}</p>
              <p className="costs-card-meta">
                {formatShortDate(data.currentMonth.periodStart)} – {formatShortDate(data.currentMonth.periodEnd)}{' '}
                (month to date)
              </p>
            </article>
            <article className="costs-summary-card">
              <h3 className="costs-card-title">Previous month</h3>
              <p className="costs-card-value">{formatCurrency(data.previousMonth.totalCost, currency)}</p>
              <p className="costs-card-meta">
                {formatShortDate(data.previousMonth.periodStart)} –{' '}
                {formatShortDate(data.previousMonth.periodEnd)}
              </p>
            </article>
            <article className="costs-summary-card">
              <h3 className="costs-card-title">Month over month</h3>
              <p className="costs-card-value costs-mom">{momCard?.primary ?? '—'}</p>
              {momCard?.secondary !== undefined && momCard.secondary !== '' ? (
                <p className="costs-card-meta">{momCard.secondary}</p>
              ) : null}
            </article>
          </section>

          <section className="costs-section" aria-labelledby="costs-forecast-heading">
            <h3 id="costs-forecast-heading" className="costs-section-title">
              Forecast (rest of month)
            </h3>
            <article className="costs-forecast-card">
              <p className="costs-forecast-amount">
                {formatCurrency(data.forecast.forecastedCost, data.forecast.currency || currency)}
              </p>
              <p className="costs-card-meta">
                {formatShortDate(data.forecast.forecastPeriodStart)} –{' '}
                {formatShortDate(data.forecast.forecastPeriodEnd)}
              </p>
            </article>
          </section>

          <section className="costs-section" aria-labelledby="costs-compare-heading">
            <h3 id="costs-compare-heading" className="costs-section-title">
              Month comparison
            </h3>
            <p className="costs-compare-lead">
              Bars scale to the larger of projected month-end spend (month to date + forecast for the rest of this
              month) and last month&apos;s full total, so you can compare likely full-month spend to the prior period.
            </p>
            <div className="costs-compare-chart">
              <div
                className="costs-compare-bars"
                role="img"
                aria-label={`This month projected ${formatCurrency(monthCompareScale.projected, currency)} (${formatCurrency(monthCompareScale.mtd, currency)} month to date plus ${formatCurrency(monthCompareScale.forecastRest, monthCompareScale.forecastCurrency)} forecast rest of month). Previous full month ${formatCurrency(monthCompareScale.previous, currency)}.`}
              >
                <div className="costs-compare-row">
                  <span className="costs-compare-label">This month (projected)</span>
                  <div className="costs-compare-track">
                    {monthCompareScale.projected > 0 ? (
                      <div
                        className="costs-compare-bar-wrap costs-compare-bar-wrap-stacked"
                        style={{ width: `${(monthCompareScale.projected / monthCompareScale.max) * 100}%` }}
                      >
                        <div className="costs-compare-stack-inline">
                          <div
                            className="costs-compare-stack-inline-seg costs-compare-stack-inline-mtd"
                            style={{
                              flex: `${Math.max(monthCompareScale.mtd, 0)} 1 0`,
                            }}
                            title={`Month to date: ${formatCurrency(monthCompareScale.mtd, currency)}`}
                          />
                          <div
                            className="costs-compare-stack-inline-seg costs-compare-stack-inline-forecast"
                            style={{
                              flex: `${Math.max(monthCompareScale.forecastRest, 0)} 1 0`,
                            }}
                            title={`Forecast rest of month: ${formatCurrency(monthCompareScale.forecastRest, monthCompareScale.forecastCurrency)}`}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="costs-compare-bar-wrap costs-compare-bar-wrap-empty" />
                    )}
                  </div>
                  <div className="costs-compare-value-cell">
                    <span className="costs-compare-value-main">
                      {formatCurrency(monthCompareScale.projected, currency)}
                    </span>
                    <span className="costs-compare-value-sub">
                      {formatCurrency(monthCompareScale.mtd, currency)} MTD +{' '}
                      {formatCurrency(monthCompareScale.forecastRest, monthCompareScale.forecastCurrency)} forecast
                    </span>
                  </div>
                </div>
                <div className="costs-compare-row">
                  <span className="costs-compare-label">Previous (full month)</span>
                  <div className="costs-compare-track">
                    {monthCompareScale.previous > 0 ? (
                      <div
                        className="costs-compare-bar-wrap costs-compare-bar-wrap-previous"
                        style={{ width: `${(monthCompareScale.previous / monthCompareScale.max) * 100}%` }}
                        title={`Previous full month: ${formatCurrency(monthCompareScale.previous, currency)}`}
                      />
                    ) : (
                      <div className="costs-compare-bar-wrap costs-compare-bar-wrap-empty" />
                    )}
                  </div>
                  <span className="costs-compare-value">
                    {formatCurrency(monthCompareScale.previous, currency)}
                  </span>
                </div>
              </div>
              <ul className="costs-compare-legend" aria-label="Month comparison legend">
                <li>
                  <span className="costs-compare-legend-swatch costs-compare-legend-swatch-mtd" />
                  Month to date
                </li>
                <li>
                  <span className="costs-compare-legend-swatch costs-compare-legend-swatch-forecast" />
                  Forecast (rest of month)
                </li>
                <li>
                  <span className="costs-compare-legend-swatch costs-compare-legend-swatch-previous" />
                  Previous month (full)
                </li>
              </ul>
            </div>
          </section>

          <section className="costs-section" aria-labelledby="costs-services-heading">
            <h3 id="costs-services-heading" className="costs-section-title">
              By AWS service
            </h3>
            <p className="costs-pie-lead">
              Slice size is each service&apos;s share of current month-to-date total. Smallest services are grouped
              into Other (about 10–15% when the data allows). Hover a slice or a legend row to see details below the
              chart; when Cost Explorer returns detail, that includes a line-by-line breakdown (for example EC2 instances,
              ECS services, or VPC usage groups). The bundled services in Other are listed there too.
            </p>
            {sortedServices.length === 0 ? (
              <p className="empty-state" role="status">
                No service-level breakdown for this period.
              </p>
            ) : (
              <div className="costs-pie-layout" onMouseLeave={(): void => setPieHoverKey(null)}>
                <figure className="costs-pie-figure">
                  <svg
                    className="costs-pie-svg"
                    viewBox="-105 -105 210 210"
                    role="img"
                    aria-label={`Cost share by AWS service (month to date): ${pieSlices
                      .map((s) => `${s.label} ${s.percent.toFixed(1)}%`)
                      .join(', ')}`}
                  >
                    {(() => {
                      let angle = -Math.PI / 2;
                      return pieSlices.map((slice) => {
                        const sweep = (slice.percent / 100) * 2 * Math.PI;
                        const d = pieSlicePath(0, 0, 88, angle, sweep);
                        angle += sweep;
                        return (
                          <path
                            key={slice.key}
                            d={d}
                            fill={slice.color}
                            className="costs-pie-slice"
                            onMouseEnter={(): void => setPieHoverKey(slice.key)}
                            onFocus={(): void => setPieHoverKey(slice.key)}
                            onBlur={(e): void => {
                              const next = e.relatedTarget;
                              const parent = e.currentTarget.parentElement;
                              if (next instanceof Node && parent !== null && parent.contains(next)) {
                                return;
                              }
                              setPieHoverKey(null);
                            }}
                            tabIndex={0}
                          />
                        );
                      });
                    })()}
                  </svg>
                  <div
                    className="costs-pie-tooltip-panel"
                    role="region"
                    aria-label="Slice details"
                    aria-live="polite"
                  >
                    {hoveredPieSlice !== null ? (
                      <div className="costs-pie-tooltip-body">
                        <p className="costs-pie-tooltip-headline">{hoveredPieSlice.headline}</p>
                        {hoveredPieSlice.breakdown !== undefined && hoveredPieSlice.breakdown.length > 0 ? (
                          <>
                            <p className="costs-pie-tooltip-sub">Breakdown</p>
                            <ul className="costs-pie-tooltip-breakdown">
                              {hoveredPieSlice.breakdown.map((line, idx) => (
                                <li key={`${line.label}-${idx}`}>
                                  <span className="costs-pie-breakdown-label">{line.label}</span>
                                  <span className="costs-pie-breakdown-amount">
                                    {formatCurrency(line.cost, line.currency || hoveredPieSlice.currency)}
                                    {line.percentOfParent !== undefined
                                      ? ` (${line.percentOfParent.toFixed(1)}% of this service)`
                                      : ''}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </>
                        ) : null}
                        {hoveredPieSlice.bundledServiceLines !== undefined &&
                        hoveredPieSlice.bundledServiceLines.length > 0 ? (
                          <ul className="costs-pie-tooltip-bundled">
                            {hoveredPieSlice.bundledServiceLines.map((line, idx) => (
                              <li key={idx}>{line}</li>
                            ))}
                          </ul>
                        ) : null}
                        {hoveredPieSlice.key === 'other' &&
                        hoveredPieSlice.bundledRows !== undefined &&
                        hoveredPieSlice.bundledRows.length > 0 ? (
                          <div className="costs-pie-tooltip-meta-section">
                            <p className="costs-pie-tooltip-sub">Line-item enrichment (per bundled service)</p>
                            <ul className="costs-pie-tooltip-meta-bundled">
                              {hoveredPieSlice.bundledRows.map((row) => (
                                <li key={row.awsService}>
                                  <span className="costs-pie-bundled-name">{row.awsService}</span>
                                  {row.breakdownMeta !== undefined ? (
                                    <>
                                      <span className="costs-pie-bundled-status">
                                        {BREAKDOWN_AVAILABILITY_HEADINGS[row.breakdownMeta.availability]}
                                        {row.breakdownMeta.code !== undefined && row.breakdownMeta.code !== ''
                                          ? ` · ${row.breakdownMeta.code}`
                                          : ''}
                                      </span>
                                      {breakdownMetaShowsLongDetail(
                                        row.breakdownMeta,
                                        row.breakdown !== undefined && row.breakdown.length > 0,
                                      ) ? (
                                        <span className="costs-pie-bundled-detail">{row.breakdownMeta.detail}</span>
                                      ) : null}
                                      {row.breakdownMeta.attemptedDimensions !== undefined &&
                                      row.breakdownMeta.attemptedDimensions.length > 0 ? (
                                        <span className="costs-pie-bundled-dims">
                                          Tried: {row.breakdownMeta.attemptedDimensions.join(' → ')}
                                        </span>
                                      ) : null}
                                    </>
                                  ) : (
                                    <span className="costs-pie-bundled-status">
                                      API did not report status (upgrade observability-api for breakdownMeta).
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                        {hoveredPieSlice.key !== 'other' && hoveredPieSlice.breakdownMeta !== undefined ? (
                          <PieSliceBreakdownMetaSection
                            meta={hoveredPieSlice.breakdownMeta}
                            hasBreakdownLines={
                              hoveredPieSlice.breakdown !== undefined && hoveredPieSlice.breakdown.length > 0
                            }
                          />
                        ) : null}
                        {hoveredPieSlice.key !== 'other' &&
                        hoveredPieSlice.breakdownMeta === undefined &&
                        (hoveredPieSlice.breakdown === undefined || hoveredPieSlice.breakdown.length === 0) ? (
                          <p className="costs-pie-tooltip-muted">
                            No line-item breakdown and no status from the API. Deploy the latest observability-api
                            costs handler so each service includes a <code className="costs-pie-inline-code">breakdownMeta</code>{' '}
                            field explaining skips, empty Cost Explorer responses, or errors.
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <p className="costs-pie-tooltip-hint">
                        Hover a pie slice or legend row for amounts and any Cost Explorer breakdown.
                      </p>
                    )}
                  </div>
                </figure>
                <ul className="costs-pie-legend" aria-label="Legend for cost by service chart">
                  {pieSlices.map((slice) => (
                    <li
                      key={slice.key}
                      className="costs-pie-legend-item"
                      onMouseEnter={(): void => setPieHoverKey(slice.key)}
                    >
                      <span
                        className="costs-pie-legend-swatch"
                        style={{ backgroundColor: slice.color }}
                        aria-hidden
                      />
                      <span className="costs-pie-legend-label">{slice.label}</span>
                      <span className="costs-pie-legend-figures">
                        {formatCurrency(slice.cost, slice.currency)}
                        <span className="costs-pie-legend-pct">{slice.percent.toFixed(1)}%</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          <section className="costs-section" aria-labelledby="costs-daily-heading">
            <h3 id="costs-daily-heading" className="costs-section-title">
              Daily spend (current month)
            </h3>
            {sortedDaily.length === 0 ? (
              <p className="empty-state" role="status">
                No daily granularity for this period.
              </p>
            ) : (
              <div
                className="costs-daily-chart"
                role="img"
                aria-label="Daily costs for the current month"
              >
                {sortedDaily.map((d) => {
                  const maxBarPx = 104;
                  const h =
                    maxDaily > 0 ? Math.max(3, (d.cost / maxDaily) * maxBarPx) : 0;
                  const dayLabel =
                    d.date.length >= 10
                      ? d.date.slice(5, 10)
                      : d.date;
                  return (
                    <div key={d.date} className="costs-daily-col">
                      <div className="costs-daily-bar-wrap">
                        <div
                          className="costs-daily-bar"
                          style={{ height: `${h}px` }}
                          title={`${d.date}: ${formatCurrency(d.cost, d.currency || currency)}`}
                        />
                      </div>
                      <span className="costs-daily-label">{dayLabel}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
